import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  Container, Grid, Divider, Chip, FormControl,
} from '@material-ui/core';
import ASBVApi from 'api/query/asbv/asbv-api';
import BackToCatalogue from 'pages/catalogues/back-to-catalogue';
import LoadContainer from 'components/load-container';
import { Autocomplete } from 'components/dropdown/autocomplete/autocomplete';
import { DropdownItem } from 'components/dropdown/dropdown-item';
import Button from 'components/button';
import { ASBVCategory } from 'api/query/asbv/asbv-category.enum';
import { ASBV } from 'api/query/asbv/asbv';
import { ASBVType } from 'api/query/asbv/asbv-type.enum';
import Dropdown from 'components/dropdown';
import { useForm } from 'react-hook-form';
import FormErrorMessage from 'components/form-error-message';
import { DeleteOutlinedIcon } from 'components/icons/delete-outlined';
import { AlertIcon } from 'components/icons/alert';
import {
  getAllowedImageExtensions, isValidImageType, getAllowedImageTypes, ImageUploadError,
} from 'shared/image-upload-utils';
import { fileToBase64 } from 'utils/file-utils';
import FooterBar from 'components/footer-bar';
import { Alert } from '@material-ui/lab';
import { CreatePenCardFormProps } from './create-pen-card-form-props';
import { initialASBVsColumns } from '../create-pen-card-utils';
import { CreatePenCardFormField } from '../create-pen-card-form-field.enum';
import { createPenCardFormValidation } from '../create-pen-card-form-validation';

export const CreatePenCardForm: React.FC<CreatePenCardFormProps> = (props) => {
  const {
    db,
    trait,
    catalogue,
    elements,
    load,
    error,
  } = props;

  const {
    register, errors, setValue, handleSubmit, watch, setError, clearError,
  } = useForm({ mode: 'onBlur', validateCriteriaMode: 'all' });
  const [visible, setVisible] = useState(true);

  // options for index dropdown and asbvs autocomplete
  const [indexItems, setIndexItems] = useState<DropdownItem[]>([]);
  const [items, setItems] = useState<DropdownItem[]>([]);

  // retrieved indexes and asbvs
  const [indexes, setIndexes] = useState<ASBV[]>([]);
  const [asbvs, setASBVs] = useState<ASBV[]>([]);

  // selected index and asbvs to show as chips in columns
  const [selectedIndex, setSelectedIndex] = useState<ASBV[]>([]);
  const [selectedASBVs, setSelectedASBVs] = useState<ASBV[]>([]);

  // combined selected index and asbvs, posted to create pen card
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>(initialASBVsColumns);

  const [logo] = watch(CreatePenCardFormField.Logo) || [];
  const [imgSrc, setImgSrc] = useState<string>();

  /**
   * Gets the indexes for selected db.
   */
  const getIndexes = useCallback((data: ASBV[]) => data.filter((a) => a.type === ASBVType.Index), []);

  /**
   * Adds a chip in table for selected index/asbv.
   * @param item selected index/asbv
   * @param isIndex is chip for index
   */
  const setColumn = (item: DropdownItem, isIndex: boolean) => {
    const columns = selectedItems;
    if (isIndex) {
      // manually set the state form value and triggers validation.
      setValue(CreatePenCardFormField.Index, item.value, true);
      // add selected index to selectedItems
      columns.splice(0, 1, item);

      // set selected index in table
      const selectedI = indexes.filter((index) => index.asbvId === item.id);
      setSelectedIndex(selectedI);

      // update items list to disable selected index
      const asbvToDisable = items.find((ind) => ind.id === item.id)!;
      if (asbvToDisable) {
        asbvToDisable.disabled = true;
      }

      // update items list to enable previously selected index
      const asbvToEnable = items.find((ind) => ind.id === selectedIndex[0]?.asbvId);
      if (asbvToEnable) {
        asbvToEnable.disabled = false;
      }
    } else if (selectedASBVs.length < 8) {
      setVisible(false);
      // add selected asbv to selectedItems
      columns.splice(selectedASBVs.length + 1, 0, item);
      columns.pop();
      // add selected asbv in table
      const selectedA = asbvs.filter((asbv) => asbv.asbvId === item.id);
      setSelectedASBVs((prev) => [...prev, selectedA[0]]);

      // if selected asbv is index, find it in index dropdown options and disable it
      const indexToDisable = indexItems.find((ind) => ind.id === item.id)!;
      if (indexToDisable) {
        indexToDisable.disabled = true;
      }
    }
    setSelectedItems(columns);
  };

  /**
   * Removes asbv from selectedItems.
   * @param asbvId id of removed asbv
   */
  const removeCol = (asbvId: string) => {
    const item = selectedItems.find((i) => i.id === asbvId)!;
    const asbvItem = selectedASBVs.find((a) => a.asbvId === asbvId)!;
    const emptyItem: DropdownItem = {
      id: '',
      value: '',
    };

    // if removed item is index, enables it in index dropdown options
    const indexItem = indexItems.find((ind) => ind.id === asbvId)!;
    if (indexItem) {
      indexItem.disabled = false;
    }

    // replaces removed item in selectedItems by empty item
    const [...selected] = selectedItems;
    selected.splice(selected.indexOf(item), 1);
    selected.push(emptyItem);
    setSelectedItems(selected);

    // removes from asbvs list (table)
    const [...ASBVS] = selectedASBVs;
    ASBVS.splice(ASBVS.indexOf(asbvItem), 1);
    setSelectedASBVs(ASBVS);
  };

  /**
   * Updates the selected ASBVs.
   *
   * If the item was removed, it is simply removed from the selected list.
   *
   * If an item was added, push it to the end.
   * @param selectedASBVItems
   * @param changedASBV
   * @param reason
   */
  const updateSelectedASBVs = (selectedASBVItems: DropdownItem[], changedASBV: DropdownItem, reason: 'Add' | 'Remove' | undefined) => {
    switch (reason) {
      case 'Remove':
        removeCol(changedASBV.id as string);
        break;
      case 'Add':
        setColumn(changedASBV, false);
        break;
      default:
        break;
    }
  };

  const submit = () => {
    elements(selectedItems, imgSrc);
  };

  useEffect(() => {
    setVisible(true);
  }, [selectedASBVs.length]);

  /**
   * Gets the catalogue logo as a base 64 string to be displayed
   * in the img tag.
   *
   * Handle custom validation here because it's not supported for input type=file.
   */
  useEffect(() => {
    // If a logo exists and is a valid file type, set the image source to the
    // base64 value of the uploaded image.
    if (logo) {
      // Check if the file is one of the supported types and set the error if not.
      const isValidType = isValidImageType(logo.type, getAllowedImageTypes());
      if (!isValidType) {
        setError(CreatePenCardFormField.Logo, 'type', ImageUploadError.FileType);
      }
      // Check if the file is under the size limit and set the error if not.
      const isValidSize = logo.size < 5e+6; // 5MB
      if (!isValidSize) {
        setError(CreatePenCardFormField.Logo, 'size', ImageUploadError.FileSize);
      }

      // If the file is a valid type and size, get the base64 string
      // and set the img src.
      if (isValidType && isValidSize) {
        const getBase64 = async () => {
          const base64 = await fileToBase64(logo);
          setImgSrc(base64);
        };
        getBase64();
      }
    } else {
      setImgSrc(undefined);
      clearError(CreatePenCardFormField.Logo);
    }
  }, [clearError, logo, setError]);

  /**
   * Manually register the dropdowns as Material-UI does not correctly expose
   * the input through the inputRef prop.
   *
   * Refer to - https://github.com/react-hook-form/react-hook-form/issues/380
   */
  useEffect(() => {
    register(CreatePenCardFormField.Index, createPenCardFormValidation[CreatePenCardFormField.Index]);
    // Manually set the value after registration
    // to ensure the value is set on the form to avoid any
    // compatibility issues with Dropdown and Select.
    setValue(CreatePenCardFormField.Index, selectedIndex[0]);
  }, [register, selectedIndex, setValue]);

  /**
   * Retrievs asbvs and splits it for index dropdown and asbv autocomplete options.
   */
  useEffect(() => {
    const getASBVsGroup = async () => {
      const data = await ASBVApi.getGroup(db, trait);
      setASBVs(data);

      const dropdownItems: DropdownItem[] = data.map((a) => ({
        id: a.asbvId,
        value: `${a.label} (${a.abbrev})`,
        disabled: false,
        group: {
          id: a.category,
          label: a.category === ASBVCategory.All ? 'All other ASBVs' : 'Default ASBV & Index based on trait',
        },
      }));
      setItems(dropdownItems);

      // Gets the indexes
      const indexesList = getIndexes(data);
      setIndexes(indexesList);

      const dropdownIndexes: DropdownItem[] = indexesList.map((i) => ({
        id: i.asbvId,
        value: `${i.label} (${i.abbrev})`,
        disabled: false,
      }));
      setIndexItems(dropdownIndexes);
    };
    if (db) {
      getASBVsGroup();
    }
  }, [db, getIndexes, trait]);

  return (
    <LoadContainer loading={!catalogue}>
      <div className="sg-create-pen-card">
        <BackToCatalogue />
        <div className="header-container">
          <Container>
            <h2>Create pen cards for {catalogue?.title}</h2>
            <div className="header__step"><b>Animals listed</b>  {catalogue?.catalogueItems.length}</div>
          </Container>
        </div>
        <form onSubmit={handleSubmit(submit)}>
          <Container>
            <div className="form-section">
              <div className="form-section__header">Fill out the details below to create pen cards for all animals listed in this catalogue</div>
              <Divider variant="fullWidth" />
              {/** Index dropdown */}
              <Grid item xs={12} sm={10} md={8} className="sg-create-pen-card__section">
                <h4>1. Select primary index to display</h4>
                <h6 className="text--normal">This index will display as the first ASBV on the pen card. You can add additional indexes in step 2.</h6>
                <Grid className="filter-grid" container item xs={9}>
                  <Dropdown
                    name={CreatePenCardFormField.Index}
                    label="primary selection index"
                    placeholder="Select an index"
                    items={indexItems}
                    item={indexItems.find((s) => s.id === selectedIndex[0]?.asbvId)}
                    error={!!errors.index}
                    onSelectionChange={(item) => setColumn(item[0], true)}
                  />
                  <FormErrorMessage className="form__error-message" errors={errors} name={CreatePenCardFormField.Index} />
                </Grid>
              </Grid>
              <Divider variant="fullWidth" />
              {/** Asbvs autocomplete and table */}
              <Grid item xs={12} sm={10} md={8} className="sg-create-pen-card__section">
                <h4>2. Customise ASBVs to display</h4>
                <h6 className="text--normal">ASBVs and indexes selected will appear in the order displayed below.</h6>
                <div className="chips__disclaimer">A maximum of 8 ASBVs &amp; indexes can be selected. Click the &apos;X&apos; to remove the ASBV or index.</div>
                <div className="chips__table">
                  <>
                    {selectedIndex && (
                      <div className="chip__col">
                        <div className="chip__head">Column 1</div>
                        <div className="chip__cell">
                          <Chip
                            className="asbv__chip"
                            label={selectedIndex[0] ? selectedIndex[0].abbrev : ''}
                          />
                        </div>
                      </div>
                    )}
                  </>
                  {selectedItems.map((column, index) => (
                    <React.Fragment key={`Column-${index + 2}`}>
                      {selectedItems[index + 1] && (
                        <div className="chip__col">
                          <div className="chip__head">Column {index + 2}</div>
                          <div className="chip__cell">
                            {selectedASBVs[index] && (
                              <Chip
                                className="asbv__chip"
                                label={selectedASBVs[index].abbrev}
                                onDelete={() => removeCol(selectedASBVs[index].asbvId)}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <Grid className="asbvs__dropdown-container" container item xs={9}>
                  {/* destorys and recreates autocomplete so it will definately update all props,
                        there was issue with disabling selected values */}
                  {visible && (
                    <Autocomplete
                      multiple
                      items={items}
                      item={selectedItems}
                      disabled={selectedASBVs.length === 7 || !selectedIndex[0]}
                      label="ASBV OR INDEX"
                      placeholder="Search for ASBV or Index"
                      onSelectionChange={(selected, changed, reason) => updateSelectedASBVs(selected, changed as DropdownItem, reason)}
                    />
                  )}
                </Grid>
              </Grid>
              <Divider variant="fullWidth" />
              {/** Optional logo */}
              <Grid item xs={12} className="sg-create-pen-card__section">
                <h4>3. Add your logo (optional)</h4>
                <Grid item xs={12} className="form-add-logo">
                  <FormControl>
                    <div className="logo">
                      <div className="logo__file-upload">
                        <label htmlFor="logo-button">
                          <input
                            id="logo-button"
                            accept={getAllowedImageExtensions().toString()}
                            name={CreatePenCardFormField.Logo}
                            type="file"
                            ref={register}
                          />
                          {!imgSrc && (
                            <>
                              <Button
                                className="logo__button"
                                variant="outlined"
                                component="div"
                              >
                                Browse files
                              </Button>
                              <div className="logo__supported-files">
                                <div>Supported files</div>
                                <div>PNG, JPG OR JPEG</div>
                              </div>
                            </>
                          )}
                        </label>
                      </div>
                      {imgSrc && (
                        <img
                          className="logo__img"
                          src={imgSrc}
                          alt="Logo"
                        />
                      )}
                    </div>
                    {imgSrc && (
                      <div className="logo__delete">
                        <Button
                          variant="outlined"
                          startIcon={<DeleteOutlinedIcon />}
                          onClick={() => setValue(CreatePenCardFormField.Logo, '')}
                        >
                          Delete Logo
                        </Button>
                      </div>
                    )}
                    {errors[CreatePenCardFormField.Logo] && (
                      <div className="logo__error">
                        <AlertIcon />{errors[CreatePenCardFormField.Logo].message}
                      </div>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </div>
          </Container>
          <FooterBar>
            <Button
              type="submit"
              loading={load}
              disabled={error}
            >create pen cards for catalogue
            </Button>
            {load && <span className="text--sm">It can take up to a couple of minutes to generate your pen cards</span>}
            {(!load && error) && <Alert severity="error">Error while generating the pdf</Alert>}
          </FooterBar>
        </form>
      </div>
    </LoadContainer>
  );
};

(CreatePenCardForm as any).whyDidYouRender = {
  customName: 'CreatePenCardForm',
};
